import React from "react";
import { useTranslation } from "react-i18next";
import { PencilIcon } from "@heroicons/react/outline";
import Button from "../v2/Button";

// One card, one level of depth. Card contents are separated by space and
// hairlines, never by a tinted box inside another tinted box.
export const CalculatorCard = ({
  title,
  subtitle,
  children,
  className = "",
}) => (
  <div
    className={`bg-white rounded-card border border-slate-200/60 shadow-sm ${className}`}
  >
    {title && (
      <div className="px-5 pt-4 pb-3">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {subtitle && (
          <p className="mt-0.5 text-[13px] text-slate-500">{subtitle}</p>
        )}
      </div>
    )}
    <div className={`px-5 pb-4 space-y-2 ${title ? "" : "pt-4"}`}>
      {children}
    </div>
  </div>
);

// A payslip is read down its amount column, so every figure shares one right
// edge and the minus hangs to the left of it. Put the sign inside the number
// and a deduction shoves its own digits out of the column.
const Amount = ({ value, sign, className = "" }) => (
  <span
    className={`ml-auto flex min-w-0 items-baseline justify-end tabular-nums text-slate-900 ${className}`}
  >
    {sign === "-" && <span className="mr-0.5 shrink-0">&#8722;</span>}
    <span className="break-words text-right">{value}</span>
  </span>
);

// Every block inside a calculator card is titled the same way, one step below
// the card title in both size and weight.
export const SectionHeading = ({ children }) => (
  <h4 className="text-[13px] font-semibold text-slate-700">{children}</h4>
);

// The answer sits at the top of the same card as the workings that produce it.
// It carries its weight through size, the way every other figure in the app
// does, so the accent is not spent on a number the card is already about.
// `action` is a link about the result itself (e.g. to a breakdown elsewhere
// on the page). It sits above the divider so it groups with the figure it
// explains rather than reading as the first line of the ledger.
export const ResultHeadline = ({ label, value, caption, action }) => (
  <div className="border-b border-slate-200 pb-4">
    <p className="text-sm font-medium text-slate-500">{label}</p>
    <p className="mt-1 break-words text-3xl font-semibold tracking-tight tabular-nums text-slate-900">
      {value}
    </p>
    {caption && <p className="mt-1 text-[13px] text-slate-500">{caption}</p>}
    {action && <div className="mt-2">{action}</div>}
  </div>
);

// Earnings and deductions are separate blocks, each closed by its own
// subtotal. The heading is what says a row adds or subtracts, so the minus
// sign is never the only cue that a figure comes off the total.
export const LedgerSection = ({ title, children }) =>
  React.Children.toArray(children).length === 0 ? null : (
    <section className="pt-3">
      <SectionHeading>{title}</SectionHeading>
      <div className="mt-0.5">{children}</div>
    </section>
  );

export const ResultRow = ({
  label,
  value,
  labelExtra,
  sign,
  bold = false,
  subtotal = false,
  total = false,
  separator = false,
}) => (
  <div
    className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1.5 ${separator ? "pb-3 mb-1 border-b border-slate-100" : ""} ${bold ? "pt-3 mt-1 border-t border-slate-100" : ""} ${subtotal ? "pt-2 mt-1 border-t border-slate-200" : ""} ${total ? "pt-3 mt-2 border-t border-slate-200" : ""}`}
  >
    <span
      className={`flex items-center gap-1.5 ${total ? "text-sm font-medium text-slate-900" : subtotal ? "text-[13px] font-medium text-slate-700" : "text-[13px] text-slate-500"}`}
    >
      {label}
      {labelExtra}
    </span>
    <Amount
      value={value}
      sign={sign}
      className={
        total
          ? "text-sm font-semibold"
          : bold || subtotal
            ? "text-sm font-semibold"
            : "text-sm font-medium"
      }
    />
  </div>
);

// Blocks are separated by space. The rule this used to draw stacked directly
// under the headline's own border whenever a card had no ledger between them,
// which read as an empty section.
export const DetailBox = ({ title, children }) =>
  React.Children.toArray(children).length === 0 ? null : (
    <div className="pt-3">
      {title && <SectionHeading>{title}</SectionHeading>}
      <div className={`space-y-1 ${title ? "mt-1.5" : ""}`}>{children}</div>
    </div>
  );

export const DetailRow = ({ label, value, sign, bold = false }) => (
  <div
    className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 ${bold ? "pt-2 mt-1 border-t border-slate-100" : ""}`}
  >
    <span className="text-[13px] text-slate-500">{label}</span>
    <Amount
      value={value}
      sign={sign}
      className={`text-sm ${bold ? "font-semibold" : ""}`}
    />
  </div>
);

export const FormulaRow = ({ label, value }) => (
  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1.5">
    <span className="text-[13px] text-slate-500">{label}</span>
    <span className="ml-auto break-words text-right text-sm font-medium tabular-nums text-slate-900">
      {value}
    </span>
  </div>
);

export const NoteBox = ({ children }) => (
  <p className="text-[13px] leading-relaxed text-slate-500">{children}</p>
);

export const InfoBadge = ({ label, value }) => (
  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1.5">
    <span className="text-[13px] text-slate-500">{label}</span>
    <Amount value={value} className="text-sm font-medium" />
  </div>
);

// The negative margin keeps this button from growing its row taller than the
// rows that have no button, while the 28px box stays a usable target.
export const InlineEditButton = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className="-my-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 transition-[color,background-color,transform] duration-150 ease-out hover:bg-slate-100 hover:text-slate-900 active:scale-[0.97] motion-reduce:transform-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
  >
    <PencilIcon className="h-4 w-4" aria-hidden="true" />
  </button>
);

export const ResetButton = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={onClick}
      className="transition-transform duration-150 ease-out active:scale-[0.98] motion-reduce:transform-none"
    >
      {t("taxCalculator.reset")}
    </Button>
  );
};

export const EmptyCalculation = ({ message }) => (
  <div className="py-10 text-center">
    <p className="text-[13px] text-slate-500">{message}</p>
  </div>
);
